import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Chip, Stack } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { api, type Announcement } from '../../lib/api';
import { useDepartmentStore } from '../../store/departmentStore';
import { AnnouncementBoard } from './AnnouncementBoard';
import { PageHeader } from './portalParts';

export function AnnouncementsPage() {
  const departments = useDepartmentStore((s) => s.departments);
  const [items, setItems] = useState<Announcement[] | null>(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api.announcements
        .list()
        .then((r) => setItems(r.announcements))
        .catch((err) => setError(err instanceof Error ? err.message : 'Could not load announcements')),
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const used = useMemo(() => new Set((items ?? []).map((a) => a.category)), [items]);
  const filtered = items && items.filter((a) => filter === 'all' || a.category === filter);

  return (
    <>
      <PageHeader
        title="Announcements"
        description="HR news, policy updates, IT notices and events from across AST."
      />
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {items && items.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 3 }}>
          <Chip
            label="All"
            onClick={() => setFilter('all')}
            color={filter === 'all' ? 'primary' : 'default'}
            variant={filter === 'all' ? 'filled' : 'outlined'}
          />
          {departments
            .filter((d) => used.has(d.id))
            .map((d) => (
              <Chip
                key={d.id}
                label={d.name}
                onClick={() => setFilter(d.id)}
                color={filter === d.id ? 'primary' : 'default'}
                variant={filter === d.id ? 'filled' : 'outlined'}
              />
            ))}
        </Stack>
      )}
      <AnnouncementBoard
        title={filter === 'all' ? 'All announcements' : (departments.find((d) => d.id === filter)?.name ?? '') + ' announcements'}
        icon={<CampaignOutlinedIcon />}
        items={filtered}
        onChanged={() => void load()}
        defaultCategory={filter === 'all' ? undefined : filter}
        emptyText="No announcements yet."
      />
    </>
  );
}
